/* eslint-disable react-refresh/only-export-components */
import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { getToolOnboardingConfig, getToolOnboardingStorageKey, type ToolOnboardingConfig } from './toolOnboarding';

type ToolOnboardingContextValue = {
  currentConfig: ToolOnboardingConfig | null;
  isOpen: boolean;
  currentStepIndex: number;
  open: () => void;
  close: () => void;
  skipForFuture: () => void;
  enableAuto: () => void;
  prevStep: () => void;
  nextStep: () => void;
  goToStep: (index: number) => void;
  isAutoEnabled: boolean;
};

const ToolOnboardingContext = createContext<ToolOnboardingContextValue | null>(null);

function readAutoEnabled(pathname: string) {
  try {
    const value = localStorage.getItem(getToolOnboardingStorageKey(pathname));
    if (value === null) return true;
    return value === '1';
  } catch {
    return true;
  }
}

function writeAutoEnabled(pathname: string, enabled: boolean) {
  try {
    localStorage.setItem(getToolOnboardingStorageKey(pathname), enabled ? '1' : '0');
  } catch {
    // no-op
  }
}

function ToolOnboardingModal({
  config,
  isOpen,
  stepIndex,
  onClose,
  onSkipFuture,
  onPrevStep,
  onNextStep,
  onGoToStep,
}: {
  config: ToolOnboardingConfig;
  isOpen: boolean;
  stepIndex: number;
  onClose: () => void;
  onSkipFuture: () => void;
  onPrevStep: () => void;
  onNextStep: () => void;
  onGoToStep: (index: number) => void;
}) {
  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowRight') onNextStep();
      if (event.key === 'ArrowLeft') onPrevStep();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose, onNextStep, onPrevStep]);

  if (!isOpen) return null;

  const step = config.steps[stepIndex];

  return createPortal(
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 90,
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        background: 'rgba(0,0,0,0.6)', padding: '12px',
        backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
      }}
    >
      <div style={{
        width: '100%', maxWidth: '36rem',
        borderRadius: 20, border: '1px solid var(--ix-border-strong)',
        background: 'var(--ix-bg-base)', color: 'var(--ix-text-primary)',
        boxShadow: '0 20px 40px rgba(0,0,0,0.5)',
        overflow: 'hidden',
      }}>
        {/* Header compact */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid var(--ix-border-subtle)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.2em', color: 'var(--ix-text-tertiary)' }}>Guide</span>
            <span style={{ fontSize: 9, color: 'var(--ix-border-strong)' }}>·</span>
            <span style={{ fontSize: 14, fontWeight: 600 }}>{config.label}</span>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button type="button" onClick={onSkipFuture} style={{ fontSize: 11, padding: '3px 10px', borderRadius: 99, border: '1px solid var(--ix-border-strong)', background: 'transparent', color: 'var(--ix-text-tertiary)', cursor: 'pointer' }}>
              Ne plus montrer
            </button>
            <button type="button" onClick={onClose} style={{ fontSize: 11, padding: '3px 10px', borderRadius: 99, border: '1px solid var(--ix-border-strong)', background: 'transparent', color: 'var(--ix-text-secondary)', cursor: 'pointer' }}>
              ✕
            </button>
          </div>
        </div>

        {/* Contenu étape */}
        <div style={{ padding: '16px 16px 12px' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 10 }}>
            <span style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.18em', color: 'var(--ix-text-tertiary)' }}>{step.eyebrow}</span>
            <span style={{ fontSize: 10, color: 'var(--ix-border-strong)' }}>·</span>
            <span style={{ fontSize: 10, color: 'var(--ix-text-tertiary)' }}>{stepIndex + 1}/{config.steps.length}</span>
          </div>
          <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>{step.title}</h3>
          <p style={{ fontSize: 13, lineHeight: 1.55, color: 'var(--ix-text-secondary)', marginBottom: 12 }}>{step.summary}</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {step.bullets.map((bullet) => (
              <div key={bullet} style={{ borderRadius: 10, border: '1px solid var(--ix-border-strong)', background: 'var(--ix-bg-surface)', padding: '8px 12px', fontSize: 13, color: 'var(--ix-text-secondary)' }}>
                {bullet}
              </div>
            ))}
          </div>
        </div>

        {/* Footer nav */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderTop: '1px solid var(--ix-border-subtle)' }}>
          <div style={{ display: 'flex', gap: 6 }}>
            <button type="button" onClick={onPrevStep} disabled={stepIndex === 0} style={{ fontSize: 11, padding: '4px 12px', borderRadius: 99, border: '1px solid var(--ix-border-strong)', background: 'transparent', color: 'var(--ix-text-secondary)', cursor: stepIndex === 0 ? 'not-allowed' : 'pointer', opacity: stepIndex === 0 ? 0.4 : 1 }}>
              ←
            </button>
            <button type="button" onClick={onNextStep} style={{ fontSize: 11, padding: '4px 12px', borderRadius: 99, border: '1px solid var(--ix-border-strong)', background: 'transparent', color: 'var(--ix-text-secondary)', cursor: 'pointer' }}>
              {stepIndex === config.steps.length - 1 ? '↺' : '→'}
            </button>
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            {config.steps.map((item, index) => (
              <button
                key={`dot-${item.title}-${index}`}
                type="button"
                onClick={() => onGoToStep(index)}
                style={{ height: 6, borderRadius: 99, border: 'none', cursor: 'pointer', padding: 0, transition: 'all 0.15s', ...(index === stepIndex ? { width: 20, background: 'rgb(232 255 60)' } : { width: 6, background: 'var(--ix-border-strong)' }) }}
                aria-label={`Étape ${index + 1}`}
              />
            ))}
          </div>
          <button type="button" onClick={onClose} style={{ fontSize: 12, padding: '4px 14px', borderRadius: 99, background: 'var(--ix-bg-surface)', border: '1px solid var(--ix-border-strong)', color: 'var(--ix-text-primary)', cursor: 'pointer', fontWeight: 500 }}>
            Continuer
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export function ToolOnboardingProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [openState, setOpenState] = useState(false);
  const [activePath, setActivePath] = useState<string | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const currentConfig = useMemo(() => getToolOnboardingConfig(location.pathname), [location.pathname]);
  const isAutoEnabled = activePath ? readAutoEnabled(activePath) : true;

  useEffect(() => {
    if (!currentConfig) {
      setOpenState(false);
      setActivePath(null);
      setCurrentStepIndex(0);
      return;
    }

    const params = new URLSearchParams(location.search);
    const forceOpen = params.get('onboarding') === '1' || params.get('guide') === '1';
    const shouldAutoOpen = forceOpen || readAutoEnabled(currentConfig.path);

    setActivePath(currentConfig.path);
    setCurrentStepIndex(0);

    if (shouldAutoOpen) {
      setOpenState(true);
    } else {
      setOpenState(false);
    }
  }, [currentConfig, location.search]);

  function open() {
    if (!currentConfig) return;
    setActivePath(currentConfig.path);
    setCurrentStepIndex(0);
    setOpenState(true);
  }

  function close() {
    setOpenState(false);
  }

  function skipForFuture() {
    if (activePath) {
      writeAutoEnabled(activePath, false);
    } else if (currentConfig) {
      writeAutoEnabled(currentConfig.path, false);
    }
    setOpenState(false);
  }

  function enableAuto() {
    if (activePath) {
      writeAutoEnabled(activePath, true);
    } else if (currentConfig) {
      writeAutoEnabled(currentConfig.path, true);
    }
  }

  function prevStep() {
    setCurrentStepIndex((value) => Math.max(0, value - 1));
  }

  function nextStep() {
    if (!currentConfig) return;
    setCurrentStepIndex((value) => (value >= currentConfig.steps.length - 1 ? 0 : value + 1));
  }

  function goToStep(index: number) {
    if (!currentConfig) return;
    const clamped = Math.max(0, Math.min(index, currentConfig.steps.length - 1));
    setCurrentStepIndex(clamped);
  }

  const activeConfig = activePath ? getToolOnboardingConfig(activePath) : currentConfig;
  const value: ToolOnboardingContextValue = {
    currentConfig,
    isOpen: openState,
    currentStepIndex,
    open,
    close,
    skipForFuture,
    enableAuto,
    prevStep,
    nextStep,
    goToStep,
    isAutoEnabled,
  };

  return (
    <ToolOnboardingContext.Provider value={value}>
      {children}
      {activeConfig ? (
        <ToolOnboardingModal
          config={activeConfig}
          isOpen={openState}
          stepIndex={currentStepIndex}
          onClose={close}
          onSkipFuture={skipForFuture}
          onPrevStep={prevStep}
          onNextStep={nextStep}
          onGoToStep={goToStep}
        />
      ) : null}
    </ToolOnboardingContext.Provider>
  );
}

export function useToolOnboarding() {
  const context = useContext(ToolOnboardingContext);
  if (!context) {
    throw new Error('useToolOnboarding must be used within ToolOnboardingProvider');
  }
  return context;
}
